import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { api } from "@/lib/api";

const Avatar = ({ photo, name }) => {
  const isUrl = photo && /^https?:\/\//i.test(photo);
  if (isUrl) return <img src={photo} alt={name} width="44" height="44" loading="lazy" decoding="async" className="h-11 w-11 rounded-full object-cover shrink-0" />;
  return (
    <span className="grid place-items-center h-11 w-11 rounded-full bg-secondary text-orange-600 text-lg shrink-0" aria-hidden="true">
      {photo || (name ? name.trim()[0].toUpperCase() : "★")}
    </span>
  );
};

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data.testimonials || [])).catch(() => {});
  }, []);

  if (!items.length) return null;

  const scroll = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  };

  const Arrow = ({ dir, tid }) => (
    <button
      onClick={() => scroll(dir)}
      className="grid place-items-center h-10 w-10 rounded-full border border-border bg-card text-foreground hover:border-orange-400 hover:text-orange-700 transition-colors"
      aria-label={dir < 0 ? "Previous testimonials" : "Next testimonials"}
      data-testid={tid}
    >
      {dir < 0 ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </button>
  );

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="home-testimonials">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-orange-700 font-mono">What clients say</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">Trusted by founders worldwide</h2>
        </div>
        {items.length > 1 && (
          <div className="hidden sm:flex gap-2"><Arrow dir={-1} tid="testimonials-prev" /><Arrow dir={1} tid="testimonials-next" /></div>
        )}
      </div>

      <div
        ref={ref}
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="testimonials-track"
      >
        {items.map((t) => (
          <figure
            key={t.id}
            className="snap-start shrink-0 w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)] rounded-2xl border border-border bg-card card-soft p-6 flex flex-col"
            data-testid={`testimonial-${t.id}`}
          >
            <Quote className="h-6 w-6 text-orange-500/50" />
            <blockquote className="mt-3 text-[15px] leading-relaxed text-foreground flex-1">{t.text}</blockquote>
            <figcaption className="mt-5 flex items-center gap-3">
              <Avatar photo={t.photo} name={t.name} />
              <div className="min-w-0">
                <p className="font-medium text-foreground truncate">{t.name}</p>
                {t.company && <p className="text-sm text-muted-foreground truncate">{t.company}</p>}
              </div>
            </figcaption>
          </figure>
        ))}
      </div>

      {items.length > 1 && (
        <div className="mt-5 flex sm:hidden justify-center gap-2"><Arrow dir={-1} tid="testimonials-prev-m" /><Arrow dir={1} tid="testimonials-next-m" /></div>
      )}
    </section>
  );
}
